import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';

// Adaptado de petrol-ng (C:\git\gitlab\petrol-ng\src\components\drawer\drawer.ts).
// Dos diferencias a propósito respecto al original, por necesidades de
// nuestro caso de uso (panel de instructivos, plan/plan-28):
//
// 1. El original se monta/desmonta con `@if (open) { <app-drawer>... }`
//    desde el consumidor — cada apertura crea una instancia nueva. Acá se
//    agregó el input `open` y el panel queda SIEMPRE montado (solo se
//    oculta con CSS): si el contenido proyectado tiene una carga async
//    adentro (como `c-card-instructivo`), no se re-dispara esa carga cada
//    vez que se abre.
// 2. Sin overlay oscuro que tape el resto de la pantalla ni cierre al
//    clickear afuera — es un panel "no modal", pensado para quedar abierto
//    mientras se sigue trabajando en el resto de la pantalla.

export type DrawerSide = 'left' | 'right';

let uid = 0;
function nextId(): string {
  uid += 1;
  return `petrol-drawer-title-${uid}`;
}

const SIDE_POSITION: Record<DrawerSide, string> = {
  left: 'left-0',
  right: 'right-0',
};

const SIDE_ENTER_TRANSLATE: Record<DrawerSide, string> = {
  left: '-translate-x-full',
  right: 'translate-x-full',
};

/**
 * Drawer — panel lateral no modal que se desliza desde el borde izquierdo o
 * derecho. Se controla con el input `open`; queda montado permanentemente
 * (el `<ng-content>` no se destruye/recrea en cada apertura).
 */
@Component({
  selector: 'petrol-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './drawer.component.html',
})
export class PetrolDrawerComponent {
  protected readonly titleId = nextId();

  readonly title = input('');
  readonly side = input<DrawerSide>('right');
  readonly width = input('360px');
  readonly open = input.required<boolean>();

  readonly closed = output<void>();

  /** Sigue en `true` durante la animación de salida, recién pasa a `false`
   * (y el panel se oculta del todo con `hidden`) cuando termina. */
  protected readonly visible = signal(false);
  protected readonly entered = signal(false);

  protected readonly sideClasses = computed(() => SIDE_POSITION[this.side()]);
  protected readonly enterTranslate = computed(
    () => SIDE_ENTER_TRANSLATE[this.side()],
  );

  constructor() {
    effect(() => {
      if (this.open()) {
        this.visible.set(true);
        requestAnimationFrame(() => this.entered.set(true));
      } else if (this.visible()) {
        this.entered.set(false);
        setTimeout(() => this.visible.set(false), 200);
      }
    });
  }

  protected onCloseClick(): void {
    this.closed.emit();
  }
}
